import {
  STUDIO_FORMATS,
  STUDIO_LANGUAGES,
  STUDIO_PREFERENCE_SLOT_COUNT,
  STUDIO_TEMPLATES,
  type StudioPreferenceSlot,
  type StudioPreferences,
  type StudioPreferencesStore
} from '../constants';

const STORAGE_KEY_PREFIX = 'hot-topic-studio-preferences:';

const createEmptySlots = (): StudioPreferenceSlot[] =>
  Array.from({ length: STUDIO_PREFERENCE_SLOT_COUNT }, () => null);

const isLegacyPreferences = (value: any): value is StudioPreferences =>
  Boolean(value) &&
  typeof value === 'object' &&
  !Array.isArray(value.slots) &&
  (typeof value.template === 'string' || typeof value.locale === 'string' || typeof value.format === 'string');

const sanitizeSlotIndex = (index: any): number => {
  const parsed = Number(index);
  if (!Number.isInteger(parsed) || parsed < 0 || parsed >= STUDIO_PREFERENCE_SLOT_COUNT) {
    return 0;
  }
  return parsed;
};

const isEmptySlotMarker = (value: any): boolean =>
  value == null || value === false || value.empty === true || value.__empty === true;

export const sanitizeStudioPreferences = (preferences?: Partial<StudioPreferences> | null): StudioPreferences | null => {
  if (preferences == null || typeof preferences !== 'object' || Array.isArray(preferences) || isEmptySlotMarker(preferences)) {
    return null;
  }

  const locale = STUDIO_LANGUAGES.some(language => language.id === preferences.locale)
    ? preferences.locale!
    : undefined;
  const format = STUDIO_FORMATS[preferences.format as keyof typeof STUDIO_FORMATS] ? preferences.format! : undefined;
  const template = STUDIO_TEMPLATES.some(item => item.id === preferences.template) ? preferences.template! : undefined;

  if (!locale && !format && !template && !preferences.themeColor && !preferences.profileName && !preferences.watermarkLogo) {
    return null;
  }

  return {
    name: typeof preferences.name === 'string' ? preferences.name.trim().slice(0, 24) : '',
    locale: locale || 'zh-Hant-HK',
    format: format || 'post',
    template: template || 'classic',
    themeColor: typeof preferences.themeColor === 'string' ? preferences.themeColor : '',
    profileName: typeof preferences.profileName === 'string' ? preferences.profileName.slice(0, 48) : '',
    watermarkLogo: preferences.watermarkLogo || null
  };
};

export const ensureStudioPreferenceSlots = (slots?: StudioPreferenceSlot[] | null): StudioPreferenceSlot[] => {
  const nextSlots = createEmptySlots();
  (slots || []).slice(0, STUDIO_PREFERENCE_SLOT_COUNT).forEach((slot, index) => {
    nextSlots[index] = sanitizeStudioPreferences(slot);
  });
  return nextSlots;
};

export const normalizeStudioPreferencesStore = (value: any): StudioPreferencesStore => {
  if (value && Array.isArray(value.slots)) {
    const slots = createEmptySlots();
    value.slots.forEach((slot: any, index: number) => {
      if (isEmptySlotMarker(slot)) {
        return;
      }
      const sanitized = sanitizeStudioPreferences(slot);
      if (!sanitized) {
        return;
      }
      // Prefer explicit slotIndex so sparse/compacted arrays keep the right seat.
      const slotIndex =
        slot && Number.isInteger(Number(slot.slotIndex)) ? sanitizeSlotIndex(slot.slotIndex) : sanitizeSlotIndex(index);
      slots[slotIndex] = sanitized;
    });

    let activeSlot = sanitizeSlotIndex(value.activeSlot);
    if (!slots[activeSlot]) {
      const firstFilled = slots.findIndex(Boolean);
      activeSlot = firstFilled >= 0 ? firstFilled : 0;
    }

    return { activeSlot, slots };
  }

  if (isLegacyPreferences(value)) {
    const slots = createEmptySlots();
    slots[0] = sanitizeStudioPreferences(value);
    return { activeSlot: 0, slots };
  }

  return { activeSlot: 0, slots: createEmptySlots() };
};

// Persist without JSON/MongoDB null holes. Empty seats use { empty: true, slotIndex }.
export const serializeStudioPreferencesStore = (store: StudioPreferencesStore) => {
  const slots = ensureStudioPreferenceSlots(store.slots);
  let activeSlot = sanitizeSlotIndex(store.activeSlot);
  if (!slots[activeSlot]) {
    const firstFilled = slots.findIndex(Boolean);
    activeSlot = firstFilled >= 0 ? firstFilled : 0;
  }

  return {
    activeSlot,
    slots: slots.map((slot, slotIndex) =>
      slot
        ? {
            ...slot,
            slotIndex
          }
        : {
            empty: true,
            slotIndex
          }
    )
  };
};

export const getStudioPreferencesStorageKey = (userId: string): string => `${STORAGE_KEY_PREFIX}${userId}`;

export const readStudioPreferencesStore = (userDetails: any): StudioPreferencesStore => {
  // Prefer localStorage: it is written synchronously on save/delete and avoids
  // races where an in-flight updateUserDetails response briefly looks empty.
  if (typeof window !== 'undefined' && userDetails?._id) {
    try {
      const stored = window.localStorage.getItem(getStudioPreferencesStorageKey(userDetails._id));
      if (stored) {
        return normalizeStudioPreferencesStore(JSON.parse(stored));
      }
    } catch (_error) {
      // Fall through to remote preferences.
    }
  }

  if (userDetails?.hotTopicStudioPreferences) {
    return normalizeStudioPreferencesStore(userDetails.hotTopicStudioPreferences);
  }

  return normalizeStudioPreferencesStore(null);
};

export const persistStudioPreferencesStore = (userId: string | undefined, store: StudioPreferencesStore): void => {
  if (typeof window === 'undefined' || !userId) {
    return;
  }
  window.localStorage.setItem(getStudioPreferencesStorageKey(userId), JSON.stringify(serializeStudioPreferencesStore(store)));
};

export const clearStudioPreferencesStore = (userId: string | undefined): void => {
  if (typeof window === 'undefined' || !userId) {
    return;
  }
  window.localStorage.removeItem(getStudioPreferencesStorageKey(userId));
};

export const getActiveStudioPreferences = (store: StudioPreferencesStore): StudioPreferences | undefined => {
  const active = store.slots[store.activeSlot];
  if (active) {
    return active;
  }
  return store.slots.find(Boolean) || undefined;
};

export const hasAnyStudioPreferences = (store: StudioPreferencesStore): boolean => store.slots.some(Boolean);

export const getDefaultSlotName = (slotIndex: number, formatMessage: (descriptor: { id: string }, values?: any) => string): string =>
  formatMessage({ id: 'hot-topic-studio-layout-slot-default' }, { number: slotIndex + 1 });

export const buildStudioPreferencesSnapshot = (input: {
  name?: string;
  locale: string;
  format: string;
  template: string;
  themeColor?: string;
  profileName?: string;
  watermarkLogo?: StudioPreferences['watermarkLogo'];
}): StudioPreferences => ({
  name: (input.name || '').trim().slice(0, 24),
  locale: input.locale,
  format: input.format,
  template: input.template,
  themeColor: input.themeColor || '',
  profileName: (input.profileName || '').slice(0, 48),
  watermarkLogo: input.watermarkLogo || null
});

export const areStudioPreferencesEqual = (
  left?: Partial<StudioPreferences> | null,
  right?: Partial<StudioPreferences> | null
): boolean => {
  if (!left || !right) {
    return left === right;
  }

  return (
    (left.name || '') === (right.name || '') &&
    left.locale === right.locale &&
    left.format === right.format &&
    left.template === right.template &&
    (left.themeColor || '') === (right.themeColor || '') &&
    (left.profileName || '') === (right.profileName || '') &&
    (left.watermarkLogo || null) === (right.watermarkLogo || null)
  );
};
